import { IconName } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import {
    getCustomerContactInfo,
    getCustomerCreditCheck,
    getCustomerExistingAccounts,
    getCustomerLegalOwnerIndividual,
    getCustomerLookupPerformed,
    getIsPrepaid,
    hasVerifiedIdentification,
} from "./customerSelectors";
import { requiresCreditCheck as requiresCreditCheckSelector } from "./creditCheckSelectors";
import { CART_ITEM_SUBSCRIPTION_TYPES, CART_ITEM_TYPE, PartialPick } from "../../../../../constants";
import { ICartItemWithBundleId, RootState } from "../../../../../models";
import {
    getAllItemsOfType,
    State as CheckoutState,
    getAllHardwareBundleItems,
    getActiveSubscription,
} from "../../../checkout/state/selectors/cartSelectors";
import {
    getSubscription,
    getSubscriptions,
    isSubscriptionHandsetCommitment,
    isSubscriptionMbbCommitment,
} from "../../../checkout/state/selectors/subscriptionsSelectors";
import PAGES from "../../../../../constants/pages";

export type State = PartialPick<RootState, "retailer" | "checkout">;

type ValidationMessage = {
    icon: IconName;
    message?: string;
    status: "error" | "warning" | "info" | "success";
};

export const isCustomerStepValidated = (state: State): ValidationMessage | null => {
    const hasVerified = hasVerifiedIdentification(state);
    const legalOwnerIndividual = getCustomerLegalOwnerIndividual(state);
    const creditCheck = getCustomerCreditCheck(state);
    const isPrepaid = getIsPrepaid(state);
    const existingAccounts = getCustomerExistingAccounts(state);
    const isExistingCustomer = existingAccounts.length > 0;
    const subscriptions = getSubscriptions(state as CheckoutState);
    const subscriptionValues = Object.values(subscriptions);
    const allSubscriptionsAreScoped =
        subscriptionValues.length > 0 &&
        subscriptionValues.every(
            (sub) =>
                sub.existingSubscriptionUpdateScope === "sim" ||
                sub.existingSubscriptionUpdateScope === "productUser" ||
                sub.existingSubscriptionUpdateScope === "vas",
        );
    const requiresCreditCheck = !allSubscriptionsAreScoped && requiresCreditCheckSelector(state);
    const hasLegalOwner = !_isEmpty(legalOwnerIndividual?.firstName);
    const contactInfo = getCustomerContactInfo(state);
    const manualResult = getCustomerLookupPerformed(state);
    const isCreditCheckOk =
        creditCheck?.performed &&
        creditCheck.decision !== "DENIED" &&
        creditCheck.decision !== "SCORE_NOT_AVAILABLE" &&
        creditCheck.decision !== "INVALID_NATIONAL_IDENTITY_NUMBER";
    // For existing customers: we don't require a successful credit check, only customer lookup (legal owner)
    const hasPerformedCheck = isExistingCustomer
        ? (manualResult || isCreditCheckOk) && hasLegalOwner && (!creditCheck?.performed || isCreditCheckOk)
        : requiresCreditCheck
          ? isCreditCheckOk
          : hasLegalOwner || isPrepaid;

    if (!hasVerified) {
        return {
            message: "Identifikasjon av kunde mangler. Vennligst gå tilbake til Kunde fanen og se over.",
            status: "error",
            icon: "alert",
        };
    }

    if (!hasPerformedCheck && !isExistingCustomer) {
        return requiresCreditCheck
            ? {
                  message:
                      "Kredittsjekk av kunde mangler eller er avslått. Vennligst gå tilbake til Kunde fanen og se over.",
                  status: "error",
                  icon: "alert",
              }
            : {
                  message: "Kundeopplysninger for eier mangler. Vennligst gå tilbake til Kunde fanen og se over.",
                  status: "error",
                  icon: "alert",
              };
    }

    if (!isExistingCustomer && !contactInfo?.email) {
        return {
            message: "E-post som kontaktinformasjon er påkrevd. Vennligst gå tilbake til Kunde-fanen og se over.",
            status: "error",
            icon: "alert",
        };
    }

    if (!manualResult && !creditCheck?.performed && isExistingCustomer) {
        return {
            message: "Kundeoppslag mangler. Vennligst hent kundedata.",
            status: "error",
            icon: "alert",
        };
    }

    return { status: "success", icon: "check-circle-filled" };
};

export const isSubscriptionStepValidated = (state: State): ValidationMessage | null => {
    const subscriptionInCart = getAllItemsOfType(
        state as CheckoutState,
        ...CART_ITEM_SUBSCRIPTION_TYPES,
    ) as ICartItemWithBundleId[];
    const draftSubscriptionsInCart = getAllItemsOfType(
        state as CheckoutState,
        CART_ITEM_TYPE.SUBSCRIPTION_DRAFT,
    ) as ICartItemWithBundleId[];
    const subscriptions = getSubscriptions(state as CheckoutState);
    const activeSubscription = getActiveSubscription(state as CheckoutState);

    if (subscriptionInCart.length === 0) {
        return null;
    }

    if (
        activeSubscription &&
        typeof location !== "undefined" &&
        location.pathname === PAGES.RETAILER.ORDER.SUBSCRIPTION.URL
    ) {
        return null;
    }

    if (subscriptionInCart.length > 0 && draftSubscriptionsInCart.length > 0) {
        return {
            message:
                "Et eller flere abonnementer er ikke fullført. Vennligst gå tilbake til Abonnement fanen og se over.",
            status: "warning",
            icon: "alert",
        };
    }

    Object.values(subscriptions).forEach((subscription) => {
        const hasPhoneNumber = subscription.phoneNumber?.selectedPhoneNumber;
        const hasProductUser = subscription.productUser?.firstName && subscription.productUser?.lastName;

        if (!hasPhoneNumber) {
            return {
                message:
                    "Et eller flere abonnement mangler telefonnummer. Vennligst gå tilbake til Abonnement fanen og se over.",
                status: "warning",
                icon: "alert",
            };
        }

        if (!hasProductUser) {
            return {
                message:
                    "Et eller flere abonnement mangler bruker informasjon. Vennligst gå tilbake til Abonnement fanen og se over.",
                status: "warning",
                icon: "alert",
            };
        }
    });

    return { status: "success", icon: "check-circle-filled" };
};

export const isEquipmentStepValidated = (state: State): ValidationMessage | null => {
    const checkoutState = state as CheckoutState;
    const subscriptionInCart = getAllItemsOfType(
        checkoutState,
        ...CART_ITEM_SUBSCRIPTION_TYPES,
    ) as ICartItemWithBundleId[];
    const draftSubscriptionsInCart = getAllItemsOfType(
        checkoutState,
        CART_ITEM_TYPE.SUBSCRIPTION_DRAFT,
    ) as ICartItemWithBundleId[];

    if (subscriptionInCart.length === 0) {
        return null;
    }

    if (draftSubscriptionsInCart.length > 0) {
        return null;
    }

    const bundlesInCart = getAllHardwareBundleItems(checkoutState);
    const bundleSubscriptionsAdded = subscriptionInCart.filter((cartItem) => {
        const sub = getSubscription(checkoutState, cartItem.bundleId);
        const isSimChangeOrPUChange =
            sub?.existingSubscriptionUpdateScope === "sim" ||
            sub?.existingSubscriptionUpdateScope === "productUser" ||
            sub?.existingSubscriptionUpdateScope === "vas";
        const hasCommitment =
            isSubscriptionHandsetCommitment(checkoutState, cartItem.bundleId) ||
            isSubscriptionMbbCommitment(checkoutState, cartItem.bundleId);
        return hasCommitment && !isSimChangeOrPUChange;
    });

    if (bundlesInCart.length !== bundleSubscriptionsAdded.length) {
        return {
            message:
                "Et eller flere abonnement mangler tilhørende utstyr i handlekurven. Vennligst gå til Utstyr fanen og se over.",
            status: "warning",
            icon: "alert",
        };
    }

    return { status: "success", icon: "check-circle-filled" };
};
